import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Form, Button, Image } from 'react-bootstrap';
import { getSiteSettings, updateSiteSettings } from '../../services/settingsService';
import { requestAdminPasswordReset, resetAdminPassword, verifyAdminPasswordResetCode, closeAllSessions } from '../../services/authService';
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaYoutube } from 'react-icons/fa';
import { message, Modal } from 'antd'; // Add antd message and Modal

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [requestingCode, setRequestingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [passwordStep, setPasswordStep] = useState('idle');
  const [closingSessions, setClosingSessions] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user?.email) {
          setPasswordForm(prev => ({
            ...prev,
            email: user.email
          }));
        }
      } catch (err) {
        console.warn('Kullanici bilgileri okunamadi:', err);
      }
    }
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await getSiteSettings();
      if (response?.isSuccess) {
        setSettings({
          ...response.data,
          logoBase64: '',
          faviconBase64: '',
          footerBase64: '',
          cityMapBase64: '',
          organizationSchemaBase64: '',
          faviconUrl: response.data.favicon
        });
        setError(null);
      } else {
        setError(response?.resultMessage || 'Ayarlar yüklenirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Settings fetch error:', err);
      setError('Ayarlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordFieldChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRequestPasswordCode = async () => {
    const email = passwordForm.email?.trim();
    if (!email) {
      message.warning('E-posta adresi bulunamadı. Yönetici bilgilerinizi kontrol edin.');
      return;
    }

    setPasswordForm(prev => ({
      ...prev,
      code: '',
      newPassword: '',
      confirmNewPassword: ''
    }));
    setPasswordStep('idle');
    setCodeSent(false);
    setRequestingCode(true);
    try {
      const response = await requestAdminPasswordReset(email);
      if (response?.isSuccess) {
        setCodeSent(true);
        setPasswordStep('code');
        message.success('Doğrulama kodu e-posta adresinize gönderildi.');
      } else {
        message.error(response?.resultMessage || 'Doğrulama kodu gönderilirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Sifre sifirlama kodu istegi hatasi:', err);
      message.error('Doğrulama kodu gönderilirken bir hata oluştu.');
    } finally {
      setRequestingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    const email = passwordForm.email?.trim();
    const code = passwordForm.code?.trim();

    if (!code) {
      message.warning('Doğrulama kodunu girmelisiniz.');
      return;
    }

    if (!email) {
      message.warning('E-posta adresi bulunamadı. Yönetici bilgilerinizi kontrol edin.');
      return;
    }

    setVerifyingCode(true);
    try {
      const response = await verifyAdminPasswordResetCode(email, code);
      if (response?.isSuccess) {
        setPasswordStep('password');
        message.success('Kod doğrulandı. Yeni şifrenizi belirleyebilirsiniz.');
      } else {
        message.error(response?.resultMessage || 'Doğrulama kodu geçersiz veya süresi dolmuş.');
      }
    } catch (err) {
      console.error('Kod dogrulama hatasi:', err);
      message.error('Doğrulama kodu kontrol edilirken bir hata oluştu.');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleCloseAllSessions = async () => {
    Modal.confirm({
      title: 'Oturumları Kapat',
      content: 'Tüm açık oturumları kapatmak istediğinize emin misiniz? Bu işlem tüm kullanıcıları yeniden giriş yapmaya zorlar.',
      okText: 'Evet',
      okType: 'danger',
      cancelText: 'Hayır',
      onOk: async () => {
        setClosingSessions(true);
        try {
          const response = await closeAllSessions();
          if (response?.isSuccess) {
            message.success('Tüm oturumlar kapatıldı. Lütfen yeniden giriş yapın.');
            localStorage.clear();
            window.location.href = '/login';
          } else {
            message.error(response?.resultMessage || 'Oturumlar kapatılamadı. Lütfen tekrar deneyin.');
          }
        } catch (err) {
          console.error('Oturum kapatma hatası:', err);
          message.error('Oturumlar kapatılamadı. Lütfen tekrar deneyin.');
        } finally {
          setClosingSessions(false);
        }
      }
    });
  };

  const handleResetPassword = async () => {
    const email = passwordForm.email?.trim();
    const code = passwordForm.code?.trim();
    const newPassword = passwordForm.newPassword;
    const confirmPassword = passwordForm.confirmNewPassword;

    if (!code) {
      message.warning('Doğrulama kodu zorunludur.');
      return;
    }

    if (!email) {
      message.warning('E-posta adresi bulunamadı. Yönetici bilgilerinizi kontrol edin.');
      return;
    }

    if (!newPassword) {
      message.warning('Yeni şifreyi girmelisiniz.');
      return;
    }

    if (newPassword.length < 6) {
      message.warning('Yeni şifre en az 6 karakter olmalıdır.');
      return;
    }

    if (newPassword !== confirmPassword) {
      message.warning('Yeni şifre ve tekrarı aynı olmalıdır.');
      return;
    }

    setResettingPassword(true);
    try {
      const response = await resetAdminPassword(email, code, newPassword);
      if (response?.isSuccess) {
        message.success('Şifreniz başarıyla güncellendi.');
        setPasswordForm(prev => ({
          ...prev,
          code: '',
          newPassword: '',
          confirmNewPassword: ''
        }));
        setCodeSent(false);
        setPasswordStep('idle');
      } else {
        message.error(response?.resultMessage || 'Şifre güncellenirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Sifre guncelleme hatasi:', err);
      message.error('Şifre güncellenirken bir hata oluştu.');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleFileChange = async (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64 = await convertToBase64(file);
        const urlField = fieldName.replace('Base64', 'Url');
        
        setSettings(prev => ({
          ...prev,
          [fieldName]: base64,
          [urlField]: URL.createObjectURL(file)
        }));
      } catch (error) {
        console.error('Error converting file:', error);
        message.error('Dosya yüklenirken bir hata oluştu.');
      }
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Return the complete data URL including MIME type
        resolve(reader.result);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await updateSiteSettings(settings);
      if (response.isSuccess) {
        const msg = response.resultMessage || 'Ayarlar başarıyla güncellendi.';
        message.success(msg);
        fetchSettings(); // Yeni ayarları yükle
      } else {
        const errorMessage = response.resultMessage || 'Ayarlar güncellenirken bir hata oluştu.';
        message.error(errorMessage);
      }
    } catch (error) {
      message.error('Ayarlar güncellenirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">Yükleniyor...</div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Site Ayarları</h2>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
        </Button>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-4 mb-4">
          <Col md={12}>
            <Card className="h-100">
              <Card.Header>
                <h5 className="mb-0">Oturum Yönetimi</h5>
              </Card.Header>
              <Card.Body className="d-flex flex-column flex-lg-row align-items-start justify-content-between gap-3">
                <div>
                  <p className="mb-2">
                    Panelde açık olan tüm oturumları kapatabilirsiniz. Bu işlemden sonra tüm kullanıcıların yeniden
                    giriş yapması gerekir.
                  </p>
                  <small className="text-muted">
                    Özellikle kaybolan cihazlar veya güvenlik ihlallerinde bu işlemi kullanabilirsiniz.
                  </small>
                </div>
                <Button
                  variant="danger"
                  onClick={handleCloseAllSessions}
                  disabled={closingSessions}
                >
                  {closingSessions ? 'Oturumlar kapatılıyor...' : 'Tüm oturumları kapat'}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4">
          <Col md={6}>
            <Card className="h-100">
              <Card.Header>
                <h5 className="mb-0">Logo ve Favicon</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-4">
                  <Form.Group>
                    <Form.Label>Site Logosu</Form.Label>
                    <div className="mb-2">
                      <Image 
                        src={settings?.logoUrl} 
                        alt="Site Logo"
                        style={{ maxHeight: '100px' }}
                      />
                    </div>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'logoBase64')}
                    />
                  </Form.Group>
                </div>

                <div className="mb-4">
                  <Form.Group>
                    <Form.Label>Footer Logo</Form.Label>
                    <div className="mb-2">
                      <Image 
                        src={settings?.footerLogoUrl} 
                        alt="Footer Logo"
                        style={{ maxHeight: '100px' }}
                      />
                    </div>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'footerBase64')}
                    />
                  </Form.Group>
                </div>

                <div>
                  <Form.Group>
                    <Form.Label>Favicon</Form.Label>
                    <div className="mb-2">
                      <Image 
                        src={settings?.faviconUrl} 
                        alt="Favicon"
                        style={{ maxHeight: '32px' }}
                      />
                    </div>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'faviconBase64')}
                    />
                  </Form.Group>
                </div>

                <div className="mt-4 p-3 border rounded">
                  <h5 className="mb-3">Panel Sifresi</h5>
                  <p className="text-muted">
                    Sifrenizi yenilemek icin once dogrulama kodu gonderin. Kod dogrulandiktan sonra yeni sifrenizi belirleyebilirsiniz.
                  </p>
                  <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={handleRequestPasswordCode}
                      disabled={requestingCode || !passwordForm.email}
                    >
                      {requestingCode ? 'Kod Gonderiliyor...' : 'Dogrulama Kodu Gonder'}
                    </Button>
                    {codeSent && (
                      <span className="text-success small">Kod gonderildi, e-postanizi kontrol edin.</span>
                    )}
                  </div>

                  {passwordStep !== 'idle' && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Dogrulama Kodu</Form.Label>
                        <Form.Control
                          type="text"
                          name="code"
                          value={passwordForm.code}
                          onChange={handlePasswordFieldChange}
                          placeholder="E-postaniza gelen kod"
                          disabled={passwordStep === 'password'}
                        />
                      </Form.Group>
                      {passwordStep === 'code' && (
                        <Button
                          variant="primary"
                          type="button"
                          onClick={handleVerifyCode}
                          disabled={verifyingCode}
                          className="mb-3"
                        >
                          {verifyingCode ? 'Kod Kontrol Ediliyor...' : 'Kodu Dogrula'}
                        </Button>
                      )}
                    </>
                  )}

                  {passwordStep === 'password' && (
                    <>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Yeni Sifre</Form.Label>
                            <Form.Control
                              type="password"
                              name="newPassword"
                              value={passwordForm.newPassword}
                              onChange={handlePasswordFieldChange}
                              placeholder="Yeni sifre"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Yeni Sifre (Tekrar)</Form.Label>
                            <Form.Control
                              type="password"
                              name="confirmNewPassword"
                              value={passwordForm.confirmNewPassword}
                              onChange={handlePasswordFieldChange}
                              placeholder="Yeni sifre tekrar"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Button
                        variant="primary"
                        type="button"
                        className="mt-3"
                        onClick={handleResetPassword}
                        disabled={resettingPassword}
                      >
                        {resettingPassword ? 'Sifre Guncelleniyor...' : 'Sifreyi Guncelle'}
                      </Button>
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="h-100">
              <Card.Header>
                <h5 className="mb-0">Genel Bilgiler</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-4">
                  <Form.Label>Site Başlığı</Form.Label>
                  <Form.Control
                    type="text"
                    name="siteTitle"
                    value={settings?.siteTitle || ''}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Telif Hakkı Metni</Form.Label>
                  <Form.Control
                    type="text"
                    name="copyrightDescription"
                    value={settings?.copyrightDescription || ''}
                    onChange={handleChange}
                  />
                </Form.Group>

                <div className="mb-4">
                  <h6>Renk Kodları</h6>
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Renk 1</Form.Label>
                        <div className="d-flex gap-2 align-items-center">
                          <Form.Control
                            type="text"
                            name="colorCode1"
                            value={settings?.colorCode1 || ''}
                            onChange={handleChange}
                            placeholder="Renk kodu"
                          />
                          <div 
                            className="color-preview"
                            style={{ 
                              backgroundColor: `#${settings?.colorCode1}`,
                              width: '32px',
                              height: '32px',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px'
                            }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Renk 2</Form.Label>
                        <div className="d-flex gap-2 align-items-center">
                          <Form.Control
                            type="text"
                            name="colorCode2"
                            value={settings?.colorCode2 || ''}
                            onChange={handleChange}
                            placeholder="Renk kodu"
                          />
                          <div 
                            className="color-preview"
                            style={{ 
                              backgroundColor: `#${settings?.colorCode2}`,
                              width: '32px',
                              height: '32px',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px'
                            }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Renk 3</Form.Label>
                        <div className="d-flex gap-2 align-items-center">
                          <Form.Control
                            type="text"
                            name="colorCode3"
                            value={settings?.colorCode3 || ''}
                            onChange={handleChange}
                            placeholder="Renk kodu"
                          />
                          <div 
                            className="color-preview"
                            style={{ 
                              backgroundColor: `#${settings?.colorCode3}`,
                              width: '32px',
                              height: '32px',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px'
                            }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="mb-4">
                  <h6 className="mb-3">Şehir Haritası ve Organizasyon Şeması</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Şehir Haritası</Form.Label>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          {settings?.cityMapUrl && (
                            <Image
                              src={settings.cityMapUrl}
                              alt="Şehir Haritası"
                              style={{ maxWidth: '100px', height: 'auto' }}
                            />
                          )}
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'cityMapBase64')}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Organizasyon Şeması</Form.Label>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          {settings?.organizationSchemaUrl && (
                            <Image
                              src={settings.organizationSchemaUrl}
                              alt="Organizasyon Şeması"
                              style={{ maxWidth: '100px', height: 'auto' }}
                            />
                          )}
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'organizationSchemaBase64')}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div>
                  <h6 className="mb-3">Sosyal Medya Bağlantıları</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FaFacebook className="me-2" />
                          Facebook
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="facebookUrl"
                          value={settings?.facebookUrl || ''}
                          onChange={handleChange}
                          placeholder="Facebook URL"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FaTwitter className="me-2" />
                          Twitter
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="twitterUrl"
                          value={settings?.twitterUrl || ''}
                          onChange={handleChange}
                          placeholder="Twitter URL"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FaInstagram className="me-2" />
                          Instagram
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="instagramUrl"
                          value={settings?.instagramUrl || ''}
                          onChange={handleChange}
                          placeholder="Instagram URL"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FaLinkedin className="me-2" />
                          LinkedIn
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="linkedinUrl"
                          value={settings?.linkedinUrl || ''}
                          onChange={handleChange}
                          placeholder="LinkedIn URL"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FaYoutube className="me-2" />
                          YouTube
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="youtubeUrl"
                          value={settings?.youtubeUrl || ''}
                          onChange={handleChange}
                          placeholder="YouTube URL"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="mt-4">
                  <h6 className="mb-3">Linkler</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Bilgi Edinme Hakkı Linki</Form.Label>
                        <Form.Control
                          type="text"
                          name="rightToInformationUrl"
                          value={settings?.rightToInformationUrl || ''}
                          onChange={handleChange}
                          placeholder="Bilgi Edinme Hakkı URL"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Evlilik İşlemleri Linki</Form.Label>
                        <Form.Control
                          type="text"
                          name="marriageProceduresUrl"
                          value={settings?.marriageProceduresUrl || ''}
                          onChange={handleChange}
                          placeholder="Evlilik İşlemleri URL"
                        />
                      </Form.Group>
                    </Col>
                
               
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Beyaz Masa Linki</Form.Label>
                        <Form.Control
                          type="text"
                          name="whiteDeskUrl"
                          value={settings?.whiteDeskUrl || ''}
                          onChange={handleChange}
                          placeholder="Beyaz Masa URL"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Kuaför Randevu Linki</Form.Label>
                        <Form.Control
                          type="text"
                          name="barberUrl"
                          value={settings?.barberUrl || ''}
                          onChange={handleChange}
                          placeholder="Kuaför Randevu URL"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>E-Ödeme Linki</Form.Label>
                        <Form.Control
                          type="text"
                          name="ePayUrl"
                          value={settings?.ePayUrl || ''}
                          onChange={handleChange}
                          placeholder="E-Ödeme URL"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default Settings;








